import React from 'react';
import { useCarouselContext } from '../Carousel.context';
import { FixMeLater } from '../../../utils';
import { STATIC_STYLES, useDynamicStyles } from './CarouselViewport.styles';
import { CarouselArrowButtons, CarouselArrowButtonsProps } from '../CarouselArrowButtons/CarouselArrowButtons';
import { Styler, StylerProps } from '../../Styler';

export interface CarouselViewportProps extends StylerProps {
    /**
     * The child `Slide` components and any navigation controls.
     */
    children?: React.ReactNode;

    /**
     * If `true` arrow buttons will be dispayed at the viewport edges.
     * @default true
     */
    withArrowButtons?: boolean;

    /**
     * Additonal properties applied to the arrow buttons.
     * @default false
     */
    ArrowButtonProps?: Partial<CarouselArrowButtonsProps>;
}

export const CarouselViewport = React.forwardRef(function CarouselViewport(
    props: CarouselViewportProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { children, withArrowButtons = true, ArrowButtonProps, ...other } = props;

    const { emblaRefElement, orientation, overflow, slideSpacing, visibleSlides, handleKeydown, title } =
        useCarouselContext();

    const dynamicStyles = useDynamicStyles(slideSpacing, visibleSlides);

    return (
        <Styler
            ref={ref}
            css={[dynamicStyles, STATIC_STYLES]}
            data-carousel-root={true}
            // We make the viewport focusable to facilitate keyboard navigation
            role="region"
            aria-roledescription="carousel"
            aria-label={title}
            onKeyDownCapture={handleKeydown}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            {...other}
        >
            {/* Embla viewport element */}
            <div ref={emblaRefElement} data-carousel-viewport={true} data-overflow={overflow}>
                <div data-carousel-container={true} data-orientation={orientation}>
                    {children}
                </div>
            </div>
            {withArrowButtons && <CarouselArrowButtons {...ArrowButtonProps} />}
        </Styler>
    );
});
